/* eslint-disable jsx-a11y/anchor-is-valid */

import * as Yup from 'yup'
import ReCAPTCHA from 'react-google-recaptcha'
import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import clsx from 'clsx'
import moment from 'moment'
import { Alert } from 'react-bootstrap-v5'
import { register, confirmRegistration, resendConfirmationCode } from '../redux/AuthCRUD'
import { useFormik } from 'formik'
import { useAuth } from 'app/context/auth-context'
import { States } from 'app/helpers/MiscUtils'
import { Button, Modal } from 'react-bootstrap-v5'
import TnC from './TnC'
import PrivacyPolicy from './PrivacyPolicy'
import Confirmation from './Confirmation'
import { config } from '../../../../config'
import { VJFPUserService } from 'app/services/VJFPUserService'

// Constants
const RESEND_TIMER_DURATION = 60
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''

// Types
interface RegistrationProps {
  roles: string
  existingEmail?: string
  afterLoginHandler?: () => void

  signInHandler?: () => void
  orgId?: string
  jobFairId?: string
}

interface FormValues {
  firstname: string
  lastname: string
  email: string
  password: string
  changepassword: string
  city: string
  state: string
  zip: string
  phone: string
  acceptTerms: boolean

  acceptCommunications: boolean

  confirmationCode: string
  recaptcha: string | null
}

// Utility functions
const formatPhoneNumber = (value: string): string => {
  const phoneNumber = value.replace(/\D/g, '')
  if (phoneNumber.length === 0) return ''
  if (phoneNumber.length <= 3) return `(${phoneNumber}`
  if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`
}

const getSponsorId = (): string | undefined => {
  return new URLSearchParams(window.location.search).get('sponsorId') || undefined
}

const getJobFairId = (): string | undefined => {
  return new URLSearchParams(window.location.search).get('jobFairId') || undefined
}

// Initial form values
const getInitialValues = (existingEmail?: string): FormValues => ({
  firstname: '',
  lastname: '',
  email: existingEmail || '',
  password: '',
  changepassword: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  acceptTerms: false,
  acceptCommunications: false,
  confirmationCode: '',
  recaptcha: null,
})

// Validation schema
const createValidationSchema = (isRegistered: boolean) => {
  const baseSchema = {
    firstname: Yup.string()
      .min(3, 'Minimum 3 symbols')
      .max(50, 'Maximum 50 symbols')
      .required('First name is required'),
    lastname: Yup.string()
      .min(3, 'Minimum 3 symbols')
      .max(50, 'Maximum 50 symbols')
      .required('Last name is required'),
    email: Yup.string()
      .email('Wrong email format')
      .min(3, 'Minimum 3 symbols')
      .max(100, 'Maximum 100 symbols')
      .required('Email is required'),
    city: Yup.string()
      .min(2, 'Minimum 2 characters')
      .max(50, 'Maximum 50 characters')
      .required('City is required'),
    state: Yup.string()
      .matches(/^[A-Z]{2}$/, 'Must be 2 letter state code (e.g. NY)')
      .required('State is required'),
    zip: Yup.string()
      .matches(/^\d{5}(-\d{4})?$/, 'Must be valid US ZIP code')
      .required('ZIP code is required'),
    phone: Yup.string()
      .matches(/^\(\d{3}\) \d{3}-\d{4}$/, 'Must be format (XXX) XXX-XXXX')
      .required('Phone number is required'),
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters long')
      .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
      .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .matches(/\d/, 'Password must contain at least one number')
      .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character')
      .required('Password is required'),
    changepassword: Yup.string()
      .required('Password confirmation is required')
      .when('password', {
        is: (val: string) => val && val.length > 0,
        then: Yup.string().oneOf(
          [Yup.ref('password')],
          "Password and Confirm Password didn't match"
        ),
      }),
    acceptTerms: Yup.bool().required('You must accept the terms and conditions'),
    acceptCommunications: Yup.bool(),
    recaptcha: Yup.mixed()
      .nullable()
      .test('recaptcha-required', 'Please verify you are not a robot', (value) => {
        return value !== null && value !== ''
      }),
  }

  const confirmationSchema = {
    confirmationCode: Yup.string()
      .min(6, 'Confirmation code must be 6 digits')
      .max(6, 'Confirmation code must be 6 digits')
      .required('Confirmation code is required'),
  }

  return Yup.object().shape(isRegistered ? { ...baseSchema, ...confirmationSchema } : baseSchema)
}

// Error handling utility
const getErrorMessage = (error: any, isConfirmation = false): string => {
  if (!error)
    return isConfirmation
      ? 'Confirmation failed. Please try again.'
      : 'Registration process has failed'

  const errorMessages = isConfirmation
    ? {
      CodeMismatchException: 'The confirmation code you entered is incorrect. Please try again.',
      ExpiredCodeException: 'The confirmation code has expired. Please request a new code.',
      UserNotFoundException: 'No account found for this email. Please register again.',
      NotAuthorizedException: 'This account is already confirmed. Please sign in.',
      LimitExceededException: 'Attempt limit exceeded, please try again later.',
    }
    : {
      UsernameExistsException:
        'An account with this email address already exists. Please sign-up using a different email address.',
      InvalidPasswordException: 'Password does not meet the requirements.',
      InvalidParameterException: 'Invalid registration parameters. Please check your input.',
    }

  return (
    errorMessages[error.code as keyof typeof errorMessages] ||
    error.message ||
    (isConfirmation ? 'Confirmation failed. Please try again.' : 'Registration process has failed')
  )
}

// Form field component - moved outside to prevent recreation on each render
const FormField = ({
  label,
  name,
  type = 'text',
  placeholder,
  required = false,
  children,
  formik,
  readOnly = false,
}: {
  label: string
  name: keyof FormValues
  type?: string
  placeholder?: string
  required?: boolean
  children?: React.ReactNode
  formik: any
  readOnly?: boolean
}) => (
  <div className='fv-row mb-7'>
    <label className={`fw-bold fs-6 mb-2 ${required ? 'required' : ''}`}>{label}</label>
    {children || (
      <input
        type={type}
        placeholder={placeholder}
        {...formik.getFieldProps(name)}
        className={clsx(
          'form-control form-control-lg form-control-solid py-3 px-4 rounded-2 fs-6',
          {
            'is-invalid': formik.touched[name] && formik.errors[name],
            'is-valid': formik.touched[name] && !formik.errors[name],
          }
        )}
        readOnly={readOnly}
      />
    )}
    {formik.touched[name] && formik.errors[name] && (
      <div className='fv-plugins-message-container'>
        <div className='fv-help-block'>
          <span role='alert'>{formik.errors[name]}</span>
        </div>
      </div>
    )}
  </div>
)

// Password field component - moved outside to prevent recreation
const PasswordField = ({
  name,
  label,
  show,
  onToggle,
  formik,
}: {
  name: keyof FormValues
  label: string
  show: boolean
  onToggle: () => void
  formik: any
}) => (
  <div className='fv-row mb-10'>
    <label className='required fw-bold fs-6 mb-2'>{label}</label>
    <div className='input-group'>
      <input
        type={show ? 'text' : 'password'}
        {...formik.getFieldProps(name)}
        className={clsx(
          'form-control form-control-lg form-control-solid py-3 px-4 rounded-2 fs-6',
          {
            'is-invalid': formik.touched[name] && formik.errors[name],
            'is-valid': formik.touched[name] && !formik.errors[name],
          }
        )}
      />
      <button
        title={show ? 'hide password' : 'show password'}
        type='button'
        className='btn btn-outline-secondary'
        onClick={onToggle}
      >
        <i className={show ? 'bi bi-eye-slash' : 'bi bi-eye'}></i>
      </button>
    </div>
    {name === 'password' && (
      <div className='alert alert-light mt-2 p-3' style={{fontSize: '0.85rem'}}>
        <strong>Password Requirements:</strong>
        <ul className='mb-0 mt-1 ps-4'>
          <li>At least 8 characters long</li>
          <li>Contains uppercase and lowercase letters</li>
          <li>Contains at least one number</li>
          <li>Contains at least one special character (!@#$%^&*(),.?":{}|&lt;&gt;)</li>
        </ul>
      </div>
    )}
    {formik.touched[name] && formik.errors[name] && (
      <div className='fv-plugins-message-container'>
        <div className='fv-help-block'>
          <span role='alert'>{formik.errors[name]}</span>
        </div>
      </div>
    )}
  </div>
)

export function Registration(props: RegistrationProps) {
  const { jobFairId: pathJobFairId } = useParams<{ jobFairId?: string }>()
  const resolvedJobFairId = props.jobFairId || pathJobFairId || getJobFairId()

  // State management
  const [loading, setLoading] = useState(false)
  const [isRegistered, setIsRegistered] = useState(false)
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [resendTimer, setResendTimer] = useState<number>(0)
  const [showTermsModal, setShowTermsModal] = useState(false)
  const [showPrivacyModal, setShowPrivacyModal] = useState(false)

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [showSmsModal, setShowSmsModal] = useState(false)
  const [smsWarningAccepted, setSmsWarningAccepted] = useState(false)

  // Read orgDetails from sessionStorage for job fair context
  const [orgDetails, setOrgDetails] = useState<any>(() => {
    try {
      const raw = sessionStorage.getItem('vjfp.orgDetails')
      return raw ? JSON.parse(raw) : null
    } catch (e) {
      console.warn('Failed to parse orgDetails from sessionStorage', e)
      return null
    }
  })

  const { login } = useAuth()
  const sponsorId = useMemo(() => getSponsorId(), [])

  // Memoized validation schema
  const validationSchema = useMemo(() => createValidationSchema(isRegistered), [isRegistered])

  // Form setup
  const formik = useFormik({
    initialValues: getInitialValues(props.existingEmail),
    validationSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      if (!isRegistered && !values.acceptCommunications && !smsWarningAccepted) {
        setShowSmsModal(true)
        setSubmitting(false)
        setLoading(false)
        return
      }

      setLoading(true)

      try {
        if (!isRegistered) {
          const email = values.email.toLowerCase()

          const existing = await new VJFPUserService().getUserExistsWithRole(email)
          if (existing?.exists) {
            setStatus('An account with this email already exists. Please sign in instead.')
            setSubmitting(false)
            setLoading(false)
            return
          }

          const jobFairId = resolvedJobFairId
          await register(
            email,
            values.firstname,
            values.lastname,
            values.phone,
            values.password,
            props.roles,
            values.acceptCommunications,
            props.orgId,
            sponsorId,
            jobFairId
          )
          setIsRegistered(true)
          setStatus('')
        }
      } catch (error: any) {
        setStatus(getErrorMessage(error, false))
      } finally {
        setLoading(false)
        setSubmitting(false)
      }
    },
  })

  // Event handlers
  const handleRecaptchaChange = useCallback(
    (value: string | null) => {
      formik.setFieldValue('recaptcha', value)
    },
    [formik]
  )

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev)
  }, [])

  const toggleConfirmPasswordVisibility = useCallback(() => {
    setShowConfirmPassword((prev) => !prev)
  }, [])

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatPhoneNumber(e.target.value)
      formik.setFieldValue('phone', formatted)
    },
    [formik]
  )

  const handleTermsClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setShowTermsModal(true)
  }, [])

  const handlePrivacyClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setShowPrivacyModal(true)
  }, [])

  // Timer effect for resend button
  useEffect(() => {
    if (resendTimer > 0) {
      const timerId = setInterval(() => {
        setResendTimer((prev) => prev - 1)
      }, 1000)
      return () => clearInterval(timerId)
    }
  }, [resendTimer])

  const isFormDisabled =
    formik.isSubmitting || !formik.isValid || (!isRegistered && !formik.values.acceptTerms)

  return (
    <div className='w-lg-1200px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto'>
      {props.roles.includes('Applicant') && orgDetails && (
        <div className='text-center mb-10'>
          <h1 className='text-dark mb-3'>Welcome to {orgDetails.orgName}'s Job Fair!</h1>
          <Alert variant='info'>
            <p>
              Event Date: {moment(orgDetails.jobFairEvent.startDate).format('MMMM D, YYYY')} , Time:{' '}
              {moment(orgDetails.jobFairEvent.startTime, 'HH:mm:ss').format('h:mm A')} (
              {orgDetails.jobFairEvent.timezone}), Duration: {orgDetails.jobFairEvent.durationHours}{' '}
              hours
            </p>
          </Alert>
          {orgDetails.sponsor &&
            (orgDetails.sponsor.sponsoredByText || orgDetails.sponsor.name) && (
              <div className='text-gray-400 fw-bold fs-4'>
                {orgDetails.sponsor.sponsoredByText || `Sponsored by: ${orgDetails.sponsor.name}`}
              </div>
            )}
        </div>
      )}
      <hr />
      <form
        className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
        noValidate
        id='kt_login_signup_form'
        onSubmit={formik.handleSubmit}
      >
        {/* Header */}
        <div className='mb-2 text-center'>
          <h1 className='text-dark mb-3'>
            {props.roles.includes('Applicant')
              ? 'Create an Account'
              : 'Create an Employer-Administrator account'}
          </h1>
          <div className='text-gray-400 fw-bold fs-4 d-flex align-items-center justify-content-center'>
            Already have an account?
            {props.roles === 'Applicant' ? (
              <Button variant='link' className='p-0 ms-2' onClick={props.signInHandler}>
                Sign in
              </Button>
            ) : (
              <Link to='/auth/login' className='ms-2 fw-bold text-primary'>
                Sign in
              </Link>
            )}
          </div>
        </div>
        {/* Status Messages */}
        {formik.status && (
          <div className='mb-lg-5 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        )}

        {/* Registration Form */}
        {!isRegistered ? (
          <div className='d-flex flex-column flex-lg-row'>
            {/* Personal Information */}
            <div
              className='flex-lg-row-fluid mb-10 mb-lg-0 me-lg-7 me-xl-10'
              style={{ maxWidth: '60%' }}
            >
              <div className='card card-flush bg-gray-100'>
                <div className='card-header'>
                  <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bold text-dark'>Personal Information</span>
                  </h3>
                </div>
                <div className='card-body pt-5'>
                  <div className='row g-5 g-xl-8'>
                    <div className='col-md-6'>
                      <FormField label='First Name' name='firstname' required formik={formik} />
                    </div>
                    <div className='col-md-6'>
                      <FormField label='Last Name' name='lastname' required formik={formik} />
                    </div>
                  </div>

                  <div className='row g-5 g-xl-8'>
                    <div className='col-md-6'>
                      <FormField
                        label='Email'
                        name='email'
                        type='email'
                        required
                        formik={formik}
                        readOnly={!!props.existingEmail}
                      />
                    </div>
                    <div className='col-md-6'>
                      <FormField
                        label='Mobile phone'
                        name='phone'
                        type='tel'
                        placeholder='(XXX) XXX-XXXX'
                        required
                        formik={formik}
                      >
                        <input
                          type='tel'
                          placeholder='(XXX) XXX-XXXX'
                          value={formik.values.phone}
                          onChange={handlePhoneChange}
                          className={clsx(
                            'form-control form-control-lg form-control-solid py-3 px-4 rounded-2 fs-6',
                            {
                              'is-invalid': formik.touched.phone && formik.errors.phone,
                              'is-valid': formik.touched.phone && !formik.errors.phone,
                            }
                          )}
                        />
                      </FormField>
                    </div>
                  </div>

                  <div className='form-check form-check-custom mt-5'>
                    <input
                      className='form-check-input align-top'
                      style={{
                        borderColor: '#009EF7',
                        borderWidth: '2px',
                        backgroundColor: formik.values.acceptCommunications ? undefined : 'white',
                      }}
                      type='checkbox'
                      id='kt_login_communications_agree'
                      {...formik.getFieldProps('acceptCommunications')}
                      checked={formik.values.acceptCommunications}
                    />
                    <label
                      className='form-check-label fw-semibold text-gray-700 mw-100'
                      htmlFor='kt_login_communications_agree'
                    >
                      <small>
                        I agree to receive SMS messages from Video Job Fairs LLC solely for the
                        purpose of being reminded of an upcoming job fair for which I had
                        registered. I understand my mobile number will not be sold or shared with
                        any third parties or affiliates for their marketing or promotional purpose.
                        I further understand that my information will only be shared with service
                        providers to operate the SMS program. Message frequency may vary. Message
                        and data rates may apply. Reply STOP to opt out, HELP for help. Consent is
                        not a condition of use / purchase. Privacy Policy :{' '}
                        <a
                          href='/privacy-policy'
                          target='_blank'
                          className='link-primary'
                          style={{ textDecoration: 'underline' }}
                        >
                          {config.vjfpBaseUrl}privacy-policy
                        </a>
                      </small>
                    </label>
                  </div>

                  <div className='separator separator-dashed my-10'></div>

                  <FormField label='City' name='city' required formik={formik} />

                  <div className='row g-5 g-xl-8'>
                    <div className='col-md-6'>
                      <FormField label='State' name='state' required formik={formik}>
                        <select
                          {...formik.getFieldProps('state')}
                          className={clsx(
                            'form-select form-select-lg form-control-solid py-3 px-4 rounded-2 fs-6',
                            {
                              'is-invalid': formik.touched.state && formik.errors.state,
                              'is-valid': formik.touched.state && !formik.errors.state,
                            }
                          )}
                        >
                          <option value=''>Select State</option>
                          {States.map((state) => (
                            <option key={state.code} value={state.code}>
                              {state.name} ({state.code})
                            </option>
                          ))}
                        </select>
                      </FormField>
                    </div>
                    <div className='col-md-6'>
                      <FormField label='ZIP Code' name='zip' required formik={formik} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Login Credentials */}
            <div className='flex-lg-auto min-w-lg-300px'>
              <div className='card card-flush'>
                <div className='card-header'>
                  <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bold text-dark'>Login Credentials</span>
                  </h3>
                </div>
                <div className='card-body pt-5'>
                  <PasswordField
                    name='password'
                    label='Password'
                    show={showPassword}
                    onToggle={togglePasswordVisibility}
                    formik={formik}
                  />
                  <PasswordField
                    name='changepassword'
                    label='Confirm Password'
                    show={showConfirmPassword}
                    onToggle={toggleConfirmPasswordVisibility}
                    formik={formik}
                  />

                  <div className='separator separator-dashed my-10'></div>

                  <div className='fv-row mb-10'>
                    <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} onChange={handleRecaptchaChange} />
                    {formik.touched.recaptcha && formik.errors.recaptcha && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.recaptcha}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className='fv-row mb-10'>
                    <div className='form-check form-check-custom'>
                      <input
                        className='form-check-input'
                        style={{
                          borderColor: '#009EF7',
                          borderWidth: '2px',
                          backgroundColor: formik.values.acceptTerms ? undefined : 'white',
                        }}
                        type='checkbox'
                        id='kt_login_toc_agree'
                        {...formik.getFieldProps('acceptTerms')}
                      />
                      <label
                        className='form-check-label fw-semibold text-gray-600'
                        htmlFor='kt_login_toc_agree'
                      >
                        I Agree to{' '}
                        <a
                          href='#'
                          className='link-primary'
                          style={{ textDecoration: 'underline' }}
                          onClick={handleTermsClick}
                        >
                          Terms and Conditions
                        </a>{' '}
                        and{' '}
                        <a
                          href='#'
                          className='link-primary'
                          style={{ textDecoration: 'underline' }}
                          onClick={handlePrivacyClick}
                        >
                          Privacy Policy
                        </a>
                      </label>
                    </div>
                    {formik.touched.acceptTerms && formik.errors.acceptTerms && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.acceptTerms}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : !isConfirmed ? (
          <div className='text-center mb-10'>
            <h3 className='text-primary mb-3'>Confirmation Required</h3>
            <p>
              Please enter the confirmation code sent to your email. A confirmation dialog will open
              to enter the code.
            </p>
          </div>
        ) : (
          /* Success Message */
          <div className='text-center mb-10'>
            <h3 className='text-success mb-3'>Confirmation Completed!</h3>
            <p>Your account has been successfully registered and confirmed.</p>
            {props.roles !== 'Applicant' && (
              <Link to='/auth/login' className='link-primary fw-bolder'>
                Go to Sign In page
              </Link>
            )}
          </div>
        )}

        {/* Submit Button - only show for initial registration step */}
        {!isConfirmed && !isRegistered && (
          <div className='d-flex justify-content-center'>
            <button
              type='submit'
              id='kt_sign_up_submit'
              className='btn btn-lg btn-primary mb-5'
              disabled={isFormDisabled}
            >
              {!loading ? (
                <span className='indicator-label'>
                  <i className='bi bi-arrow-right-circle-fill fs-2 me-2'></i>
                  {props.roles === 'Applicant' ? 'Complete Profile' : 'Register'}
                </span>
              ) : (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  Please wait...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        )}
      </form>

      <Confirmation
        asModal
        show={isRegistered && !isConfirmed}
        autoResend={false}
        email={formik.values.email.toLowerCase()}
        password={formik.values.password}
        onConfirmed={async () => {
          setIsConfirmed(true)
          try {
            await login(formik.values.email.toLowerCase(), formik.values.password)
            props.afterLoginHandler?.()
          } catch (err) {
            console.warn('Sign in after confirmation failed', err)
          }
        }}
        onCancel={() => {
          setIsRegistered(false)
          setIsConfirmed(false)
        }}
      />

      {/* Terms or Privacy Modal */}
      <Modal
        show={showTermsModal || showPrivacyModal}
        onHide={() => {
          setShowTermsModal(false)
          setShowPrivacyModal(false)
        }}
        size='lg'
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Terms and Conditions</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {showTermsModal ? <TnC /> : <PrivacyPolicy />}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            onClick={() => {
              setShowTermsModal(false)
              setShowPrivacyModal(false)
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* SMS Consent Warning Modal */}
      <Modal show={showSmsModal} onHide={() => setShowSmsModal(false)} centered>
        <Modal.Header closeButton className='border-0'>
          <Modal.Title className='text-warning fs-2'>
            <i className='bi bi-exclamation-triangle-fill text-warning fs-1 me-3'></i>
            Important Communication Notice
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='px-10 py-5'>
          <div className='text-center mb-8'>
            <div className='symbol symbol-100px symbol-circle mb-5 bg-light-warning p-5'>
              <i className='bi bi-chat-dots-fill text-warning fs-3x'></i>
            </div>
            <h2 className='fw-bold text-dark mb-3'>Stay in the Loop!</h2>
            <p className='text-gray-600 fs-5 lh-lg'>
              You haven't opted in for SMS notifications.{' '}
              <strong>You might miss critical communications</strong>, such as schedule reminders,
              and real-time updates during the job fair.
            </p>
          </div>

          <div className='bg-light-primary rounded p-5 d-flex align-items-center'>
            <i className='bi bi-shield-check text-primary fs-1 me-4'></i>
            <div className='text-gray-700 fs-7'>
              We only send job fair related updates and we will not spam you.
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className='justify-content-center border-0 pt-0 pb-10 flex-column flex-sm-row gap-3'>
          <Button
            variant='primary'
            className='btn-lg px-8 py-4 fw-bold order-1 order-sm-2'
            onClick={async () => {
              setSmsWarningAccepted(true)
              setShowSmsModal(false)
              // Ensure the field value is updated before triggering submission
              await formik.setFieldValue('acceptCommunications', true)
              formik.submitForm()
            }}
          >
            Enable SMS & Register
          </Button>
          <Button
            variant='light-warning'
            className='btn-lg px-8 py-4 fw-bold text-warning order-2 order-sm-1'
            onClick={() => {
              setSmsWarningAccepted(true)
              setShowSmsModal(false)
              // Using requestAnimationFrame or simple timeout to ensure modal closes and state settles
              window.requestAnimationFrame(() => formik.submitForm())
            }}
          >
            Proceed without SMS
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
